import React, { useEffect, useState } from "react";
import { Clapperboard, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";

const LS_KEY = "gv_cine_waitlist_joined";

// Public pre-launch waitlist for AAA Cinematic Mode. Renders only while the admin
// teaser is on and the real stream isn't live yet. Converts anonymous visitors into
// a warm launch-day audience.
export default function CinematicWaitlist() {
  const [state, setState] = useState(null); // {enabled, live, count}
  const [joined, setJoined] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === "1"; } catch { return false; }
  });
  const [email, setEmail] = useState("");
  const [emailDone, setEmailDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/cinematic/waitlist").then(({ data }) => setState(data)).catch(() => setState({ enabled: false }));
  }, []);

  if (!state || !state.enabled) return null;

  const count = state.count || 0;
  const MILESTONES = [50, 100, 250, 500, 1000, 2500, 5000, 10000];
  const reached = [...MILESTONES].reverse().find((m) => count >= m) || null;
  const next = MILESTONES.find((m) => m > count) || null;
  const prev = reached || 0;
  const progress = next ? Math.min(100, Math.round(((count - prev) / (next - prev)) * 100)) : 100;

  const join = async () => {
    if (busy) return;
    setBusy(true);
    setState((s) => ({ ...s, count: (s.count || 0) + 1 }));
    setJoined(true);
    try { localStorage.setItem(LS_KEY, "1"); } catch {}
    try { await api.post("/cinematic/waitlist", {}); } catch {}
    finally { setBusy(false); }
    toast.success("You're on the list! 🎬");
  };

  const submitEmail = async () => {
    const e = email.trim();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await api.post("/cinematic/waitlist", { email: e });
      setEmailDone(true);
      toast.success("Done — we'll email you the moment it goes live.");
    } catch { toast.error("Something went wrong"); }
    finally { setBusy(false); }
  };

  return (
    <section className="relative z-10 max-w-5xl mx-auto px-5 py-14" data-testid="cinematic-waitlist">
      <div className="relative overflow-hidden rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/15 via-orange-500/5 to-transparent p-8 sm:p-12 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/20 text-amber-300 text-xs font-bold mb-4">
          <Clapperboard className="w-3.5 h-3.5" /> COMING SOON
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold mb-3">AAA Cinematic Mode</h2>
        <p className="text-white/70 max-w-xl mx-auto mb-6">
          Movie-grade Unreal Engine visuals, streamed live to your browser — no downloads, no beefy PC.
          We're firing up the GPU cloud.
        </p>

        <div className="text-5xl sm:text-6xl font-black text-amber-300 tabular-nums" data-testid="cinematic-waitlist-count">
          {count}
        </div>
        <p className="text-sm text-white/60 mb-3">players want it — join them</p>

        {reached && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-500/20 text-orange-300 text-xs font-bold mb-4 animate-pulse" data-testid="cinematic-waitlist-milestone">
            🔥 Trending — {reached.toLocaleString()}+ players want AAA mode!
          </div>
        )}

        {next && (
          <div className="max-w-xs mx-auto mb-6" data-testid="cinematic-waitlist-progress">
            <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-700" style={{ width: `${progress}%` }} />
            </div>
            <p className="text-[11px] text-white/50 mt-1.5">{(next - count).toLocaleString()} more to hit {next.toLocaleString()} 🔥</p>
          </div>
        )}

        {joined ? (
          <div className="max-w-sm mx-auto space-y-3">
            <p className="text-emerald-400 font-semibold flex items-center justify-center gap-1.5" data-testid="cinematic-waitlist-joined">
              <Check className="w-4 h-4" /> You're on the list!
            </p>
            {emailDone ? (
              <p className="text-sm text-white/60">We'll email you at launch. 🎉</p>
            ) : (
              <div className="flex gap-2">
                <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                  placeholder="Email me at launch (optional)" className="h-10 bg-white/5 border-white/15"
                  data-testid="cinematic-waitlist-email" />
                <Button onClick={submitEmail} disabled={busy} className="rounded-full bg-amber-400 text-black hover:bg-amber-300 shrink-0" data-testid="cinematic-waitlist-email-btn">
                  Notify me
                </Button>
              </div>
            )}
          </div>
        ) : (
          <Button onClick={join} disabled={busy} size="lg"
            className="rounded-full gap-2 bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold hover:from-amber-300 hover:to-orange-400 text-base px-8"
            data-testid="cinematic-waitlist-join">
            <Sparkles className="w-5 h-5" /> I want this too
          </Button>
        )}
      </div>
    </section>
  );
}
